import { AlertTriangle } from 'lucide-react';
import type { DunningSweepAbort } from '../hooks/useDunningReview';

/** Whole hours since an ISO timestamp, floored — "for 2h" reads better than a precise duration. */
function hoursSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000));
}

const JOB_LABEL: Record<string, string> = {
  escalate: 'The escalation sweep',
  pollChargeWindow: 'The upcoming-charge advisory poll',
  pollCardExpiryWindow: 'The card-expiry advisory poll',
};

/**
 * A dunning sweep is refusing to run.
 *
 * WHY THIS IS ON THIS PAGE. A sweep aborts rather than acting on a partial set when its candidate
 * list exceeds the cap — the right call, because more candidates than expected is either a real
 * backlog needing a decision or a broken filter. But the abort used to write one ERROR line to pod
 * stdout and nothing else, so the engine could sit stopped for hours with the queue below looking
 * merely quiet. This is the difference between "no one needs reviewing" and "nothing has been
 * looked at since 14:05".
 *
 * It takes no dismiss, deliberately: the cron clears the row the moment the sweep runs again, so a
 * banner someone could dismiss would outlive the problem and teach people to ignore it.
 */
export function SweepAbortBanner({ aborts }: { aborts: DunningSweepAbort[] }) {
  if (!aborts.length) return null;

  return (
    <div className="space-y-2" data-testid="sweep-abort-banner">
      {aborts.map((a) => {
        const hours = hoursSince(a.firstSeenAt);
        return (
          <div
            key={a.job}
            role="alert"
            className="flex gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
            <div className="space-y-1">
              <p className="font-medium text-destructive">
                {JOB_LABEL[a.job] ?? a.job} has stopped — {a.candidates} candidates, cap {a.cap}
              </p>
              <p className="text-muted-foreground">
                Nothing has advanced for the accounts it covers: no notice sent, nothing limited or
                suspended. It refuses to act on a partial set, so this is either a real backlog
                that needs a decision or a broken filter.
              </p>
              <p className="text-muted-foreground">
                First seen {new Date(a.firstSeenAt).toLocaleString()} ·{' '}
                {hours > 0 ? `stuck for ${hours}h · ` : ''}
                {a.ticks} refusal{a.ticks === 1 ? '' : 's'} today
              </p>
              {a.detail ? <p className="font-mono text-xs text-muted-foreground">{a.detail}</p> : null}
              <p className="text-xs text-muted-foreground">
                Clears by itself once the sweep runs again — no need to dismiss it.
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
